import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import ColumnVisibilityDropdown from "../TMSWidgets/ColumnVisibilityDropdown";

// Import types
import {
  FilterValue,
  FilterConfig,
  FilterType,
  UnifiedTableFilterProps,
} from "./types";

// Import filter components
import { FilterMenu } from "./filters/FilterMenu";
import { FilterBadgesContainer } from "./filters/FilterBadgesContainer";

// Import individual filters for rendering options
import { LanguageFilter } from "./filters/LanguageFilter";
import { StatusFilter } from "./filters/StatusFilter";
import { SourceStatusFilter } from "./filters/SourceStatusFilter";
import { TagsFilter } from "./filters/TagsFilter";
import { CreatorFilter } from "./filters/CreatorFilter";
import { TemplateFilter } from "./filters/TemplateFilter";
import { VersionFilter } from "./filters/VersionFilter";
import { DateFilter } from "./filters/DateFilter";

// Import action components
import { SearchBar } from "./actions/SearchBar";
import { AddButton } from "./actions/AddButton";
import { RefreshButton } from "./actions/RefreshButton";
import { ShadowModeSelector } from "./actions/ShadowModeSelector";
import { ViewModeToggle } from "./actions/ViewModeToggle";
import { DownloadButton } from "./actions/DownloadButton";
import { ExportButton } from "./actions/ExportButton";
import { ExportDataButton } from "./actions/ExportDataButton";
import { CommentsButton } from "./actions/CommentsButton";
import { DeleteButton } from "./actions/DeleteButton";

/**
 * Unified Table Filter component - Main orchestrator
 * Combines search, filters, and action buttons into a cohesive filter bar
 */
const UnifiedTableFilter: React.FC<UnifiedTableFilterProps> = ({
  searchTerm,
  onSearchChange,
  availableFilters,
  activeFilters = [],
  onFiltersChange,
  columnVisibility,
  onColumnVisibilityChange,
  actions,
  shadowMode = "status",
  onShadowModeChange,
  viewMode = "grid",
  onViewModeChange,
  allowFlowView = false,
}) => {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState<FilterType | null>(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Get current filter for a specific type
  const getFilterByType = (type: FilterType) => {
    return activeFilters.find(f => f.type === type);
  };

  // Get filter config for a specific type
  const getFilterConfig = (type: FilterType) => {
    return availableFilters.find(f => f.type === type);
  };

  // Handle filter type selection
  const handleFilterTypeSelect = (type: FilterType) => {
    setActiveFilterType(type);
    setFilterMenuOpen(false);
    
    // If filter doesn't exist, create it
    if (!getFilterByType(type) && onFiltersChange) {
      onFiltersChange([...activeFilters, { type, values: [] }]);
    }
  };

  // Handle filter value toggle
  const handleFilterValueToggle = (type: FilterType, value: string) => {
    if (!onFiltersChange) return;

    const updatedFilters = [...activeFilters];
    const filterIndex = updatedFilters.findIndex(f => f.type === type);

    if (filterIndex === -1) {
      // Create new filter
      updatedFilters.push({ type, values: [value] });
    } else {
      const filter = updatedFilters[filterIndex];
      if (filter.values.includes(value)) {
        // Remove value
        filter.values = filter.values.filter(v => v !== value);
        // Remove filter if no values left
        if (filter.values.length === 0) {
          updatedFilters.splice(filterIndex, 1);
          if (activeFilterType === type) {
            setActiveFilterType(null);
          }
        }
      } else {
        // Add value
        filter.values.push(value);
      }
    }

    onFiltersChange(updatedFilters);
  };

  // Handle filter removal
  const handleRemoveFilter = (type: FilterType) => {
    if (!onFiltersChange) return;
    
    const updatedFilters = activeFilters.filter(f => f.type !== type);
    onFiltersChange(updatedFilters);
    
    if (activeFilterType === type) {
      setActiveFilterType(null);
    }
  };

  // Get filter label
  const getFilterLabel = (filter: FilterValue) => {
    const config = getFilterConfig(filter.type);
    if (!config) return filter.type;

    if (filter.values.length === 0) return config.label;
    
    if (filter.values.length === 1) {
      // Special handling for language type with flags
      if (filter.type === 'language' && config.options) {
        const option = config.options.find(o => o.value === filter.values[0]);
        return option?.flag ? `${option.flag} ${option.label}` : (option?.label || filter.values[0]);
      }
      
      const option = config.options?.find(o => o.value === filter.values[0]);
      return option?.label || filter.values[0];
    }
    
    return `${filter.values.length} ${config.label}`;
  };

  // Render filter options based on type
  const renderFilterOptions = (filter: FilterValue) => {
    const config = getFilterConfig(filter.type);
    if (!config) return null;

    const handleValueChange = (value: string) => handleFilterValueToggle(filter.type, value);

    // Render appropriate filter component based on type
    switch (filter.type) {
      case 'language':
        return <LanguageFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'status':
        return <StatusFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'sourceStatus':
        return <SourceStatusFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'tags':
        return <TagsFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'creator':
        return <CreatorFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'template':
        return <TemplateFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'version':
        return <VersionFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      case 'date':
        return <DateFilter filter={filter} config={config} onValueChange={handleValueChange} />;
      default:
        // Fallback to generic checkbox rendering
        return config.options?.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 cursor-pointer hover:bg-muted p-2 rounded"
          >
            <Checkbox
              checked={filter.values.includes(option.value)}
              onCheckedChange={() => handleValueChange(option.value)}
            />
            {option.flag && <span className="text-lg">{option.flag}</span>}
            <span className="text-sm">{option.label}</span>
          </label>
        ));
    }
  };

  return (
    <div className="flex items-center gap-2 w-full p-4 border-b border-border">  
      {/* Search Icon / Expandable Search Bar */}
      <div className="flex items-center gap-1">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          isExpanded={isSearchExpanded}
          onExpandedChange={setIsSearchExpanded}
        />

        {/* Filter Dropdown - Next to search icon */}
        {availableFilters && availableFilters.length > 0 && (
          <FilterMenu
            availableFilters={availableFilters}
            activeFilters={activeFilters}
            isOpen={filterMenuOpen}
            onOpenChange={setFilterMenuOpen}
            onFilterSelect={handleFilterTypeSelect}
          />
        )}
      </div>

      {/* Filter Badges with Drag-and-Drop */}
      <div className="flex items-center gap-2 flex-1">
        {onFiltersChange && (
          <FilterBadgesContainer
            activeFilters={activeFilters}
            activeFilterType={activeFilterType}
            onActiveFilterTypeChange={setActiveFilterType}
            onFiltersChange={onFiltersChange}
            onRemoveFilter={handleRemoveFilter}
            getFilterLabel={getFilterLabel}
            renderFilterOptions={renderFilterOptions}
            getFilterConfig={getFilterConfig}
          />
        )}
      </div>

      {/* Action Icons */}
      <div className="flex items-center gap-2">
        {/* Add Button */}
        {actions?.onAdd && (
          <>
            <AddButton onClick={actions.onAdd} />
            {/* Separator */}
            <div className="w-px h-6 bg-input mx-1" />
          </>
        )}

        {/* Refresh Button */}
        {actions?.onRefresh && (
          <RefreshButton onClick={actions.onRefresh} />
        )}

        {/* Column Visibility Dropdown */}
        {columnVisibility && onColumnVisibilityChange && (
          <ColumnVisibilityDropdown
            visibleColumns={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
        )}

        {/* Shadow Mode Selector */}
        {onShadowModeChange && (
          <ShadowModeSelector
            shadowMode={shadowMode}
            onShadowModeChange={onShadowModeChange}
          />
        )}

        {/* View Mode Toggle */}
        {onViewModeChange && (
          <>
            {/* Separator */}
            <div className="w-px h-6 bg-input mx-1" />
            <ViewModeToggle
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              allowFlowView={allowFlowView}
            />
          </>
        )}

        {/* Optional Action Buttons */}
        {actions && (
          <>
            {/* Separator */}
            <div className="w-px h-6 bg-input mx-1" />

            {/* Download Icon */}
            {actions.onDownload && (
              <DownloadButton onClick={actions.onDownload} />
            )}

            {/* Export/Import Icon */}
            {actions.onExport && (
              <ExportButton onClick={actions.onExport} />
            )}

            {/* Export Data Icon */}
            {actions.onExportData && (
              <ExportDataButton 
                onClick={actions.onExportData} 
                directExport={actions.directExport}
              />
            )}

            {/* Separator */}
            {(actions.onComments || actions.onDelete) && (
              <div className="w-px h-6 bg-input mx-1" />
            )}

            {/* Comments Icon */}
            {actions.onComments && (
              <CommentsButton onClick={actions.onComments} />
            )}

            {/* Separator */}
            {actions.onDelete && (
              <div className="w-px h-6 bg-input mx-1" />
            )}

            {/* Delete Icon */}
            {actions.onDelete && (
              <DeleteButton 
                onClick={actions.onDelete} 
                hasSelectedRows={actions.hasSelectedRows ?? false}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UnifiedTableFilter;

